import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import apiClient from '../utils/apiClient';
import { FaCheckCircle, FaTimesCircle, FaSpinner, FaArrowRight } from 'react-icons/fa';
import config from '../config';

const EmailVerification = ({ isDarkMode }) => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Verification token is missing from the URL.');
        return;
      }

      try {
        const response = await apiClient.get(
          `${config.API_BASE_URL}/auth/verify-email/${token}`
        );

        setStatus('success');
        setMessage(response.data?.message || 'Your email has been verified successfully!');
      } catch (err) {
        setStatus('error');
        const detail = err?.response?.data?.detail;

        if (typeof detail === 'string') {
          setMessage(detail);
        } else if (detail?.message) {
          setMessage(detail.message);
        } else {
          setMessage('Email verification failed. The link may have expired or is invalid.');
        }
      }
    };

    verifyEmail();
  }, [token]);

  const c = {
    card: isDarkMode ? 'bg-gray-900/90 border-gray-700' : 'bg-white/95 border-gray-200',
    text: isDarkMode ? 'text-gray-300' : 'text-gray-700',
    button: isDarkMode
      ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:from-cyan-600 hover:to-blue-600'
      : 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white hover:from-indigo-700 hover:to-blue-700',
  };

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 ${isDarkMode ? 'bg-gray-950' : 'bg-gray-100'}`}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={`w-full max-w-md p-8 rounded-2xl border shadow-2xl backdrop-blur ${c.card}`}
      >
        {/* Verifying State */}
        {status === 'verifying' && (
          <div className="text-center">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className="inline-block mb-6"
            >
              <FaSpinner className={`w-16 h-16 ${isDarkMode ? 'text-cyan-400' : 'text-indigo-600'}`} />
            </motion.div>
            <h2 className={`text-2xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Verifying Your Email
            </h2>
            <p className={c.text}>Please wait while we verify your email address...</p>
          </div>
        )}

        {/* Success State */}
        {status === 'success' && (
          <div className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15 }}
              className="inline-block mb-6"
            >
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <FaCheckCircle className="w-12 h-12 text-green-500" />
              </div>
            </motion.div>
            <h2 className={`text-2xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Email Verified!
            </h2>
            <p className={`${c.text} mb-6`}>{message}</p>
            <p className={`${c.text} text-sm mb-6`}>
              You can now sign in to your account and start exploring jobs.
            </p>
            <motion.button
              onClick={() => navigate('/login')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`w-full py-3 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2 shadow-lg ${c.button}`}
            >
              Continue to Login
              <FaArrowRight className="w-4 h-4" />
            </motion.button>
          </div>
        )}

        {/* Error State */}
        {status === 'error' && (
          <div className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15 }}
              className="inline-block mb-6"
            >
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto">
                <FaTimesCircle className="w-12 h-12 text-red-500" />
              </div>
            </motion.div>
            <h2 className={`text-2xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Verification Failed
            </h2>
            <p className={`${c.text} mb-6`}>{message}</p>
            <div className="space-y-3">
              <motion.button
                onClick={() => navigate('/signup')}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`w-full py-3 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2 shadow-lg ${c.button}`}
              >
                Try Signing Up Again
                <FaArrowRight className="w-4 h-4" />
              </motion.button>
              <motion.button
                onClick={() => navigate('/login')}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`w-full py-3 rounded-xl font-semibold transition-all duration-200 ${
                  isDarkMode
                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Back to Login
              </motion.button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default EmailVerification;
